/** The stored row to rasterize: a size variant, or the base version. */
export type RasterizeAdTarget =
  | { sizeVariantId: string }
  | { versionId: string };

/** The row a stored ad's primary render belongs to; null before its first version. */
export function assetVersionRasterTarget(asset: {
  version_id: string | null;
}): RasterizeAdTarget | null {
  return asset.version_id ? { versionId: asset.version_id } : null;
}

/** The asset a raster is posted under, and the stored row of it to rasterize. */
export type RasterizeAdSubject = {
  assetId: string;
  target: RasterizeAdTarget;
};

/**
 * The open resize as the raster needs it. A legacy resize is a size-variant
 * row of the primary; a size asset is its own asset, rasterized at the
 * selected version like the primary.
 */
export type RasterizableResize =
  | { id: string; isLegacy: true }
  | { id: string; isLegacy: false; version_id: string | null };

/**
 * The stored row the editor canvas is rendering. A legacy size view holds the
 * variant row id in `selectedVersionId`; the primary and size-asset views hold
 * a version id, or null while following the newest. Null when no stored row
 * backs the canvas: a variant the asset no longer lists, or an asset without a
 * version yet.
 */
export function resolveCanvasRasterSubject(args: {
  selectedResize: RasterizableResize | null | undefined;
  selectedVersionId: string | null | undefined;
  asset: {
    id: string;
    version_id: string | null;
    ad_brief_asset_size_variants?: ReadonlyArray<{ id: string }> | null;
  };
}): RasterizeAdSubject | null {
  const { selectedResize, selectedVersionId, asset } = args;
  if (selectedResize?.isLegacy) {
    const variant = asset.ad_brief_asset_size_variants?.find(
      (sv) => sv.id === (selectedVersionId ?? selectedResize.id),
    );
    return variant
      ? { assetId: asset.id, target: { sizeVariantId: variant.id } }
      : null;
  }
  const open = selectedResize ?? asset;
  const versionId = selectedVersionId ?? open.version_id;
  return versionId ? { assetId: open.id, target: { versionId } } : null;
}

/**
 * Tells the backend an ad render just left Hightouch (a download), so it
 * rasterizes that row server-side and fingerprints the result for matching
 * against ads launched on a platform. No pixels leave the browser.
 *
 * Fire-and-forget: failures are logged and swallowed, downloads never wait.
 */
export async function requestAdRaster({
  apiEndpoint,
  assetId,
  target,
}: {
  apiEndpoint: string;
  assetId: string;
  target: RasterizeAdTarget;
}): Promise<void> {
  try {
    const res = await fetch(`${apiEndpoint}/agents/ads/${assetId}/rasters`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(target),
    });
    if (!res.ok) {
      console.warn(`ad-raster: POST failed with ${res.status}`);
    }
  } catch (error) {
    console.warn("ad-raster: failed to request an ad raster", error);
  }
}
